import { jsx as _jsx } from "react/jsx-runtime";
import Plotly from "plotly.js-dist-min";
import React, { useEffect, useRef } from "react";
const BAR_COLOR = "#0066FF";
const BAR_TEXT_COLOR = "#ffffff";
const BAR_WIDTH = 0.7;
const BAR_CORNER_RADIUS = 3;
const BAR_GAP = 0.15;
const TICK_LABEL_FONT_SIZE = 12;
const AXIS_TITLE_FONT_SIZE = 13;
const CHART_MARGIN_BOTTOM = 60;
const CHART_MARGIN_LEFT = 60;
const CHART_MARGIN_RIGHT = 20;
const CHART_MARGIN_TOP = 20;
const Y_AXIS_RANGE_START = 0;
/**
 * Plotly bar chart for the Energy &amp; Productivity panel. The y-axis scale
 * and whether to render in-bar value labels are both driven by `chartConfig`
 * so the same component can render Material Moved (0-2000 / tonnes), Load
 * Count (0-60 / cycles), and Idle Time (0-100% / %).
 */
const EnergyProductivityChart = ({ chartConfig, series }) => {
    const containerRef = useRef(null);
    useEffect(() => {
        const node = containerRef.current;
        if (!node)
            return undefined;
        const { showInBarText, xAxisTitle, yAxis } = chartConfig;
        const xValues = series.tickLabels.map((_, index) => index);
        const trace = {
            hoverinfo: "skip",
            insidetextanchor: "end",
            marker: { color: BAR_COLOR },
            text: showInBarText ? series.data.map((value) => value.toString()) : series.data.map(() => ""),
            textfont: { color: BAR_TEXT_COLOR, size: TICK_LABEL_FONT_SIZE },
            textposition: showInBarText ? "inside" : "none",
            texttemplate: showInBarText
                ? series.unit === "%"
                    ? `%{text}${series.unit}`
                    : `%{text}<br>${series.unit}`
                : "",
            type: "bar",
            width: BAR_WIDTH,
            x: xValues,
            y: series.data
        };
        const layout = {
            barcornerradius: BAR_CORNER_RADIUS,
            bargap: BAR_GAP,
            font: { family: "Roboto, Arial, sans-serif", size: TICK_LABEL_FONT_SIZE },
            margin: { b: CHART_MARGIN_BOTTOM, l: CHART_MARGIN_LEFT, r: CHART_MARGIN_RIGHT, t: CHART_MARGIN_TOP },
            paper_bgcolor: "transparent",
            plot_bgcolor: "transparent",
            showlegend: false,
            xaxis: {
                fixedrange: true,
                showgrid: false,
                showline: false,
                tickmode: "array",
                ticktext: series.tickLabels,
                tickvals: xValues,
                title: { font: { size: AXIS_TITLE_FONT_SIZE }, text: `<b>${xAxisTitle}</b>` },
                zeroline: false
            },
            yaxis: {
                fixedrange: true,
                range: [Y_AXIS_RANGE_START, yAxis.max],
                showgrid: true,
                showline: false,
                tickmode: "array",
                ticktext: yAxis.tickText,
                tickvals: yAxis.tickValues,
                title: { font: { size: AXIS_TITLE_FONT_SIZE }, text: `<b>${yAxis.title}</b>` },
                zeroline: false
            }
        };
        const config = { displayModeBar: false, responsive: true };
        Plotly.newPlot(node, [trace], layout, config);
        const handleResize = () => {
            Plotly.Plots.resize(node);
        };
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
            Plotly.purge(node);
        };
    }, [chartConfig, series]);
    return _jsx("div", { className: "w-full h-full", "data-test": "energy-productivity-bar-chart", ref: containerRef });
};
export default EnergyProductivityChart;
